import { createApi } from '@reduxjs/toolkit/query/react';
import axios from 'axios';

// Custom base query using axios directly
// Note: We cannot use axiosFetcher here because it imports logger,
// which creates a circular dependency with the store during test initialization
const axiosBaseQuery =
  () =>
  async ({ url, method = 'POST', data }: any) => {
    try {
      const response = await (method === 'POST'
        ? axios.post('/api/dap-api', {
            url,
            data,
          })
        : axios.get('/api/dap-api', { params: { url } }));
      return { data: response?.data };
    } catch (error: any) {
      return {
        error: {
          status: error?.response?.status || 500,
          data: error?.response?.data || error.message || 'Unknown error',
        },
      };
    }
  };

export const ccPatientApi = createApi({
  reducerPath: 'ccPatientApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['CCPatient'],
  endpoints: (builder) => ({
    // Get CC Patient by ID
    getCCPatientById: builder.query({
      query: (ccId: string) => ({
        url: `/CCT/GetPatientByIdFromCC?ccId=${ccId}`,
        method: 'POST',
      }),
      providesTags: (_result, _error, ccId) => [{ type: 'CCPatient', id: ccId }],
      // Transform response to return the data directly
      transformResponse: (response: any) => response,
    }),

    // Fetch multiple patients at once (batch request)
    getCCPatientsByIds: builder.query({
      async queryFn(ccIds: string[], _queryApi, _extraOptions, fetchWithBQ) {
        const results = await Promise.allSettled(
          ccIds.map((ccId) =>
            fetchWithBQ({
              url: `/CCT/GetPatientByIdFromCC?ccId=${ccId}`,
              method: 'POST',
            })
          )
        );

        const data: Record<string, any> = {};
        const errors: string[] = [];

        results.forEach((result, index) => {
          const ccId = ccIds[index];
          if (result.status === 'fulfilled' && result.value.data) {
            data[ccId] = result.value.data;
          } else if (result.status === 'rejected') {
            errors.push(`Failed to fetch data for ccId: ${ccId}`);
          } else if (result.status === 'fulfilled' && result.value.error) {
            errors.push(`Error for ccId ${ccId}: ${result.value.error.data}`);
          }
        });

        if (errors.length > 0 && Object.keys(data).length === 0) {
          return { error: { status: 'CUSTOM_ERROR', data: errors.join(', ') } };
        }

        return { data };
      },
      providesTags: (result) =>
        result
          ? [
              ...Object.keys(result).map((ccId) => ({ type: 'CCPatient' as const, id: ccId })),
              { type: 'CCPatient', id: 'LIST' },
            ]
          : [{ type: 'CCPatient', id: 'LIST' }],
    }),
  }),
});

export const { useGetCCPatientByIdQuery, useGetCCPatientsByIdsQuery, usePrefetch } = ccPatientApi;
